import { LegendItem, SelectedLegendItemState } from '@/components';
import { PieChartDataset } from '@/components/PieChartBase';
import {
  ColorPaletteKind,
  getOverriddenProperties,
  getSeriesIdGenerator,
  LabelFormatOption,
  PieChartOverrides,
  PieChartVisualOverride,
  SeriesColorGenerator,
  SingleValue,
} from '@/core';

interface GetDatasetAndLegendProps {
  data: SingleValue[];
  aggregationPercent: number;
  seriesColorGenerator: SeriesColorGenerator;
  colorPalette: ColorPaletteKind;
  primaryColor: string;
  hasLegend?: boolean;
  overrides?: PieChartOverrides;
}

/**
 * Converts raw data into an ECharts-compatible dataset with aggregated values
 * less than or equal to a specified threshold and extracts legend items.
 */
export const getDatasetAndLegend = ({
  data,
  aggregationPercent,
  seriesColorGenerator,
  colorPalette,
  primaryColor,
  hasLegend,
  overrides,
}: GetDatasetAndLegendProps) => {
  const generateSeriesId = getSeriesIdGenerator();

  const valuesSum = data.reduce((sum, item) => sum + (item.value ?? 0), 0);
  const dataset: PieChartDataset = [];
  const legendItems: LegendItem[] = [];
  let aggregatedSeriesSum = 0;
  let aggregatedSeries: PieChartDataset = [];
  const aggregatedSeriesIds: string[] = [];
  let aggregatedSeriesColor: string = '';
  data.forEach((datum, index) => {
    const seriesName = datum.formattedName || datum.name;
    const seriesValue = datum.value ?? null;
    let seriesColor: string | undefined;
    // Check for color overrides before using color from palette
    const overriddenProperties = getOverriddenProperties<PieChartVisualOverride>(seriesName, overrides);
    if (overriddenProperties.color) {
      seriesColor = overriddenProperties.color;
    } else {
      seriesColor = seriesColorGenerator.getSeriesColor({
        kind: colorPalette,
        primaryColor,
        seriesName,
        seriesIndex: index,
      });
    }
    const seriesId = generateSeriesId(datum.name);

    const value = datum.value ?? 0; // treat nulls as 0s in calculations
    // if valuesSum is 0, set percentage to 0 so we don't end up dividing by 0
    const percent = valuesSum === 0 ? 0 : (value / valuesSum) * 100;
    if (percent <= aggregationPercent) {
      // set the color of the aggregated slice to the color of the first series in the aggregated data
      if (aggregatedSeriesColor === '') {
        aggregatedSeriesColor = seriesColor;
      }

      aggregatedSeriesSum += value;
      aggregatedSeries.push([seriesName, seriesValue, aggregatedSeriesColor, seriesId]);
      aggregatedSeriesIds.push(seriesId);
    } else {
      dataset.push([seriesName, seriesValue, seriesColor, seriesId]);
    }
    if (hasLegend) {
      legendItems.push({
        id: seriesId,
        label: seriesName,
        color: aggregatedSeriesColor.length && aggregatedSeries.length > 1 ? aggregatedSeriesColor : seriesColor,
        data: { value: seriesValue },
      });
    }
  });

  // if only one value is less than or equal to the aggregationPercent, no need to aggregate it so add it back to dataset
  if (aggregatedSeries.length === 1 && aggregatedSeries[0]) {
    dataset.push(...aggregatedSeries);
    aggregatedSeries = [];
  }

  // combine values less than or equal to aggregationPercent into a single slice and add it to the end of the array
  if (aggregatedSeries.length > 1) {
    dataset.push([`${aggregatedSeries.length} more`, aggregatedSeriesSum, aggregatedSeriesColor, aggregatedSeriesIds]);
  }

  return {
    dataset,
    aggregatedSeries: aggregatedSeries.sort((a, b) => (b[1] as number) - (a[1] as number)),
    legendItems,
  };
};

interface FilterDatasetAndAggregatedSeriesProps {
  newSelectedLegendItems: SelectedLegendItemState;
  dataset: PieChartDataset;
  aggregatedSeries: PieChartDataset;
}

/**
 * Filters the dataset and aggregated series when a legend item is clicked
 */
export const filterDatasetAndAggregatedSeries = ({
  newSelectedLegendItems,
  dataset,
  aggregatedSeries,
}: FilterDatasetAndAggregatedSeriesProps): {
  filteredDataset: PieChartDataset;
  filteredAggregatedSeries: PieChartDataset;
} => {
  if (newSelectedLegendItems === 'ALL') {
    return { filteredDataset: dataset, filteredAggregatedSeries: aggregatedSeries }; // return as is
  }

  // filter dataset and aggregated series based on selected legend items
  const filteredDataset = dataset.filter((item) => {
    const index = item[3];
    // ignore aggregated slice for now, we will handle this below
    if (Array.isArray(index)) {
      return;
    }
    return newSelectedLegendItems[index] === true;
  });
  const filteredAggregatedSeries = aggregatedSeries.filter((item) => {
    const index = item[3];
    // ignore aggregated slice for now, we will handle this below
    if (Array.isArray(index)) {
      return;
    }
    return newSelectedLegendItems[index] === true;
  });

  // The aggregated slice was filtered out above so we need some logic to know when to add it back
  const aggregatedSlice = dataset.find((item) => Array.isArray(item[3]));

  // only add back aggregated slice if it exists and more than a single item is selected
  if (aggregatedSlice && (filteredDataset.length > 1 || filteredAggregatedSeries.length > 0)) {
    const hasAggregatedSeriesChanged = filteredAggregatedSeries.length !== aggregatedSeries.length;
    // number of aggregated series selected has changed and is > 0, update the "X more" slice
    if (hasAggregatedSeriesChanged && filteredAggregatedSeries.length > 0) {
      const updatedAggregatedSeriesSum = filteredAggregatedSeries.reduce((acc, [_, num]) => acc + (num ?? 0), 0);
      const filteredIndexes: string[] = filteredAggregatedSeries.map((item) => item[item.length - 1] as string);
      filteredDataset.push([
        `${filteredAggregatedSeries.length} more`,
        updatedAggregatedSeriesSum,
        aggregatedSlice[2], // color is unchanged
        filteredIndexes,
      ]);
    } else if (!hasAggregatedSeriesChanged) {
      filteredDataset.push(aggregatedSlice);
    }
  }

  return { filteredDataset, filteredAggregatedSeries };
};

/**
 * We store the label format as an array of strings (LabelFormatOption) in the dashboard spec.
 * To make this easier to work with internally, we convert it to a string (LabelFormatString)
 */
export type LabelFormatString = 'Percent' | 'Value' | 'Both';

/**
 * Converts the label format array from the dashboard spec in to a string so that it's easier to work with internally
 */
export const convertLabelFormatArrayToString = (
  labelFormatArray: LabelFormatOption = ['Percent']
): LabelFormatString => {
  if (labelFormatArray.length === 2 && labelFormatArray.includes('Percent') && labelFormatArray.includes('Value')) {
    return 'Both';
  } else if (labelFormatArray.length === 1 && labelFormatArray.includes('Value')) {
    return 'Value';
  }
  return 'Percent';
};

/**
 * Converts a label format string to the label format array needed by the dashboard spec
 */
export const convertLabelFormatStringToArray = (labelFormatString: LabelFormatString): LabelFormatOption => {
  if (labelFormatString === 'Both') {
    return ['Percent', 'Value'];
  } else if (labelFormatString === 'Value') {
    return ['Value'];
  }
  return ['Percent'];
};
